import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPinned, CheckCircle2, Linkedin, AlertTriangle, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICES, COMPANY } from "../../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ content }) => {
  const contact = content?.contact || {
    phone: "0487 096 279",
    email: "admin@quanticom.com.au",
    address_line1: "Wollert",
    address_line2: "3750",
  };
  const services = content?.services?.length ? content.services : SERVICES;

  const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data.success) {
        setSubmitted(true);
        toast.success("Quote request sent successfully!");
        setForm({ name: "", email: "", phone: "", service_interest: "", message: "" });
      }
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section bg-[#081C3A]" data-testid="contact-section">
      <div className="container-custom">
        {/* Emergency banner */}
        <Reveal>
          <div
            className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl px-6 py-5 mb-12 bg-[#D4A017]/10 border border-[#D4A017]/40"
            data-testid="emergency-banner"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-[#D4A017] shrink-0" />
              <span className="text-white font-medium">
                24/7 Emergency Network Response — rapid mobilisation Australia-wide.
              </span>
            </div>
            <a href={`tel:${contact.phone?.replace(/\s/g, "")}`} className="btn-cyan whitespace-nowrap" data-testid="emergency-call-btn">
              <Phone className="w-4 h-4" /> Call {contact.phone}
            </a>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Info + map */}
          <Reveal>
            <div className="h-full flex flex-col">
              <span className="eyebrow mb-5">Get In Touch</span>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-white mt-4 mb-4">Request a Quote</h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Ready to start your next telecommunications project? Tell us what you need and our team will respond within 24 hours.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Phone, label: "Phone", value: contact.phone, href: `tel:${contact.phone?.replace(/\s/g, "")}`, testid: "contact-phone" },
                  { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}`, testid: "contact-email" },
                ].map((c) => (
                  <a key={c.label} href={c.href} className="glass rounded-xl p-5 hover:border-[#00D4FF]/40 transition-colors" data-testid={c.testid}>
                    <c.icon className="w-5 h-5 text-[#00D4FF] mb-3" />
                    <div className="text-xs text-slate-400 mb-1">{c.label}</div>
                    <div className="text-white font-medium text-sm break-words">{c.value}</div>
                  </a>
                ))}
                <div className="glass rounded-xl p-5 sm:col-span-2" data-testid="contact-address">
                  <MapPinned className="w-5 h-5 text-[#00D4FF] mb-3" />
                  <div className="text-xs text-slate-400 mb-1">Head Office</div>
                  <div className="text-white font-medium text-sm">
                    {contact.address_line1}, {contact.address_line2}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden border border-white/10 flex-1 min-h-[220px]">
                <iframe
                  title="Quanticom service area"
                  src="https://www.google.com/maps?q=Wollert,VIC,3750,Australia&z=9&output=embed"
                  className="w-full h-full min-h-[220px]"
                  style={{ border: 0, filter: "grayscale(0.3) invert(0.9) hue-rotate(180deg)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="coverage-map"
                />
              </div>
              <a href={COMPANY.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-slate-300 hover:text-[#00D4FF] mt-6 transition-colors" data-testid="social-linkedin">
                <Linkedin className="w-5 h-5" /> Follow us on LinkedIn
              </a>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1}>
            <div className="card-dark rounded-2xl p-8 lg:p-10 h-full">
              {submitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="contact-success">
                  <CheckCircle2 className="w-16 h-16 text-[#00D4FF] mb-5" />
                  <h3 className="text-2xl font-bold text-white mb-2">Request Sent!</h3>
                  <p className="text-slate-400 mb-6">Thank you. Our team will respond within 24 hours.</p>
                  <button onClick={() => setSubmitted(false)} className="btn-ghost" data-testid="send-another-btn">
                    Send Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="form-label-dark" htmlFor="c-name">Full Name *</label>
                      <input id="c-name" required minLength={2} className="input-dark" placeholder="John Smith"
                        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" />
                    </div>
                    <div>
                      <label className="form-label-dark" htmlFor="c-email">Email *</label>
                      <input id="c-email" type="email" required className="input-dark" placeholder="john@company.com"
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="form-label-dark" htmlFor="c-phone">Phone</label>
                      <input id="c-phone" type="tel" className="input-dark" placeholder="0400 000 000"
                        value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="input-phone" />
                    </div>
                    <div>
                      <label className="form-label-dark" htmlFor="c-service">Service</label>
                      <select id="c-service" className="input-dark cursor-pointer" value={form.service_interest}
                        onChange={(e) => setForm({ ...form, service_interest: e.target.value })} data-testid="select-service">
                        <option value="" disabled>Select a service</option>
                        {services.map((s) => (
                          <option key={s.id} value={s.id} data-testid={`service-option-${s.id}`}>{s.title}</option>
                        ))}
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="form-label-dark" htmlFor="c-message">Message *</label>
                    <textarea id="c-message" required minLength={10} rows={5} className="input-dark resize-none"
                      placeholder="Tell us about your project requirements..."
                      value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="input-message" />
                  </div>
                  <button type="submit" disabled={submitting} className="btn-cyan w-full" data-testid="submit-contact-btn">
                    {submitting ? (<><span className="spinner mr-1" /> Sending...</>) : (<>Send Request <Send className="w-4 h-4" /></>)}
                  </button>
                  <p className="text-xs text-slate-500 mt-4 text-center">We'll respond within 24 hours</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
